import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { patch } from "@/lib/api";
import { clientLabel, pickableClients, type ClientOption } from "@/lib/clients";
import { parseNumber } from "@/lib/format";
import type { BookingRow } from "@/lib/types";

const NEW = "__new__";

/** `clients` includes former ones: the row's own client may be former; choosing another client offers only non-former ones. */
export function BookingEditDialog({ row, clients, onClose }: { row: BookingRow | null; clients: ClientOption[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [clientId, setClientId] = useState<string>("");
  const [projectId, setProjectId] = useState<string>("");
  const [newProject, setNewProject] = useState("");
  const [date, setDate] = useState("");
  const [description, setDescription] = useState("");
  const [hoursText, setHoursText] = useState("");

  const own = useMemo(() => (row ? clients.find((c) => c.name === row.clientName) : undefined), [row, clients]);
  const choices = pickableClients(clients, [own?.id]);

  useEffect(() => {
    if (!row) return;
    setClientId(own ? String(own.id) : "");
    setProjectId(String(own?.projects.find((p) => p.name === row.projectName)?.id ?? ""));
    setNewProject("");
    setDate(row.date);
    setDescription(row.description);
    setHoursText(String(Math.round((row.trackedMinutes / 60) * 100) / 100).replace(".", ","));
  }, [row, own]);

  const projects = clients.find((c) => String(c.id) === clientId)?.projects ?? [];
  const hours = parseNumber(hoursText);
  const valid = !!row && description.trim() !== "" && hours !== null && hours > 0 && (projectId === NEW ? newProject.trim() !== "" : projectId !== "");

  const save = useMutation({
    mutationFn: () =>
      patch<BookingRow>(`/api/entries/${row!.id}`, {
        date,
        description: description.trim(),
        hours,
        ...(projectId === NEW ? { clientId: Number(clientId), newProjectName: newProject.trim() } : { projectId: Number(projectId) }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["entries"] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Buchung gespeichert");
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Buchung bearbeiten</DialogTitle></DialogHeader>
        <form className="contents" onSubmit={(e) => { e.preventDefault(); if (valid && !save.isPending) save.mutate(); }}>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Kunde</Label>
              <Select value={clientId} onValueChange={(v) => { setClientId(v); setProjectId(""); }}>
                <SelectTrigger><SelectValue placeholder="Kunde" /></SelectTrigger>
                <SelectContent>{choices.map((c) => <SelectItem key={c.id} value={String(c.id)}>{clientLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Projekt</Label>
              <Select value={projectId} onValueChange={setProjectId} disabled={!clientId}>
                <SelectTrigger><SelectValue placeholder="Projekt" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                  <SelectItem value={NEW}>+ Neues Projekt</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {projectId === NEW && <Input placeholder="Name des neuen Projekts" value={newProject} onChange={(e) => setNewProject(e.target.value)} />}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="d">Datum</Label>
              <Input id="d" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="h">Stunden</Label>
              <Input id="h" inputMode="decimal" value={hoursText} onChange={(e) => setHoursText(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="t">Tätigkeit</Label>
            <Textarea id="t" rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); e.currentTarget.form?.requestSubmit(); } }} />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button disabled={!valid || save.isPending} type="submit">Speichern</Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
